import { CreditCard } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { type ChangeEvent, useState } from "react";
import { Helmet } from "react-helmet-async";

import { useToast } from "@/client/hooks/use-toast";
import { adminApi } from "@/client/services/admin";

// Types for subscription object
type User = {
  email?: string;
};
type Subscription = {
  id: string | number;
  user?: User;
  planType?: string;
  status?: string;
  startsAt?: string;
  expiresAt?: string;
  downloadsUsed?: number;
};

// Helper to determine badge variant
const getSubscriptionStatusVariant = (status?: string) => {
  switch (status) {
    case "ACTIVE": {
      return "success";
    }
    case "EXPIRED": {
      return "error";
    }
    default: {
      return "secondary";
    }
  }
};

export const AdminSubscriptionsPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("");
  const [expiringSoon, setExpiringSoon] = useState<boolean>(false);
  const [extendId, setExtendId] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState<string>("30");
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-subscriptions", status, expiringSoon],
    queryFn: () =>
      adminApi.getSubscriptions({
        status: status || undefined,
        expiringSoon: expiringSoon || undefined,
      }),
  });

  const subscriptions: Subscription[] = data?.subscriptions ?? [];

  const handleExtend = async () => {
    if (!extendId || !extendDays) return;

    const days = Number.parseInt(extendDays, 10);
    if (Number.isNaN(days) || days <= 0) {
      toast({
        variant: "error",
        title: "Invalid days",
        description: "Enter a positive number.",
      });
      return;
    }

    setActionLoading(true);
    try {
      await adminApi.manageSubscription(extendId, "extend", days);
      toast({ variant: "success", title: "Subscription extended." });
      setExtendId(null);
      setExtendDays("30");
      void queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    } catch (error_: unknown) {
      const msg = error_ instanceof Error ? error_.message : "Failed to extend.";
      toast({ variant: "error", title: "Error", description: msg });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelId) return;
    setActionLoading(true);
    try {
      await adminApi.manageSubscription(cancelId, "cancel");
      toast({ variant: "success", title: "Subscription cancelled." });
      setCancelId(null);
      void queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    } catch (error_: unknown) {
      const msg = error_ instanceof Error ? error_.message : "Failed to cancel.";
      toast({ variant: "error", title: "Error", description: msg });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (id: string) => {
    setActionLoading(true);
    try {
      await adminApi.manageSubscription(id, "reactivate");
      toast({ variant: "success", title: "Subscription reactivated." });
      void queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    } catch (error_: unknown) {
      const msg = error_ instanceof Error ? error_.message : "Failed to reactivate.";
      toast({ variant: "error", title: "Error", description: msg });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Admin Subscriptions - VillageHut Resume</title>
      </Helmet>

      <h1 className="text-2xl font-semibold">Subscriptions</h1>

      <div className="flex flex-wrap items-center gap-4">
        <Select
          value={status || "all"}
          onValueChange={(v) => {
            setStatus(v === "all" ? "" : v);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={expiringSoon}
            className="rounded"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setExpiringSoon(e.target.checked);
            }}
          />
          <span className="text-sm">Expiring soon (7 days)</span>
        </label>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-5" />
            Subscription list
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-muted-foreground">Loading...</p>}
          {error && <p className="text-destructive">Failed to load subscriptions.</p>}

          {!isLoading && !error && subscriptions.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">No subscriptions found.</div>
          )}

          {!isLoading && !error && subscriptions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Plan</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Starts</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Expires</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Downloads used</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((s) => (
                    <tr key={String(s.id)} className="border-b">
                      <td className="px-4 py-3 text-sm">{s.user?.email ?? "—"}</td>
                      <td className="px-4 py-3 text-sm">{s.planType ?? "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant={getSubscriptionStatusVariant(s.status)}>
                          {s.status ?? "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {dayjs(s.startsAt).format("MMM D, YYYY")}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {dayjs(s.expiresAt).format("MMM D, YYYY")}
                      </td>
                      <td className="px-4 py-3 text-sm">{Number(s.downloadsUsed ?? 0)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {s.status === "ACTIVE" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setExtendId(String(s.id));
                                }}
                              >
                                Extend
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setCancelId(String(s.id));
                                }}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {s.status === "EXPIRED" && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => {
                                void handleReactivate(String(s.id));
                              }}
                            >
                              Reactivate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extend Subscription Dialog */}
      <AlertDialog
        open={!!extendId}
        onOpenChange={(open) => {
          if (!open) {
            setExtendId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Extend subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Enter number of days to add to the current expiry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              type="number"
              min={1}
              value={extendDays}
              placeholder="Days"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setExtendDays(e.target.value);
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={actionLoading}
              onClick={() => {
                void handleExtend();
              }}
            >
              Extend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog
        open={!!cancelId}
        onOpenChange={(open) => {
          if (!open) {
            setCancelId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel subscription</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the subscription immediately. The user will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground"
              onClick={() => {
                void handleCancel();
              }}
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
